<template>
    <b-container fluid class="overview">
        <h1 class="text-left">{{ $t('menuItemWebThemeCards') }}</h1>
        <br />
        <div class="row">
            <!-- Search field -->
            <search-box
                id="searchWebThemeCards"
                @input="$_onInput"
                extendedClass="col-md-2"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
            >
            </search-box>
            <!-- FeedbackType filter field -->
            <x-select
                :id="`selectFeedbackType`"
                :name="`selectFeedbackType`"
                @selected="$_onSelectType"
                :options="feedbackTypeOptions"
                :textDefaultOption="$t('typeFilter')"
                required
                :saveInState="true"
                :value="selectedType"
            >
            </x-select>
            <!-- Theme filter field -->
            <div class="col-md-2 form-group">
                <x-select
                    :id="`selectName`"
                    :name="`selectName`"
                    @selected="$_onSelectTheme"
                    :options="themeFilterOptions"
                    :textDefaultOption="$t('themeFilter')"
                    :saveInState="true"
                    :value="selectedTheme"
                >
                </x-select>
            </div>
            <!-- Feedback filter field -->
            <div class="col-md-2 override-left-padding form-group">
                <x-select
                    :id="`selectFeedback`"
                    :name="`selectFeedback`"
                    @selected="$_onSelectFeedback"
                    :options="feedbackFilterOptions"
                    :textDefaultOption="'Filter op terugmelding'"
                    :saveInState="true"
                    :value="selectedFeedback"
                >
                </x-select>
            </div>
            <!-- Measurement filter field -->
            <div class="col-md-2 override-left-padding form-group">
                <x-select
                    :id="`selectMeasurement`"
                    :name="`selectMeasurement`"
                    @selected="$_onSelectMeasurement"
                    :options="measurementFilterOptions"
                    :textDefaultOption="'Filter op meting'"
                    :saveInState="true"
                    :value="selectedMeasurement"
                >
                </x-select>
            </div>
            <!-- Portal filter field -->
            <div v-if="userInfo.licenses.has_portal_license">
                <x-select
                    :id="`selectPortalGroup`"
                    :name="`selectPortalGroup`"
                    @selected="$_onSelectPortalGroup"
                    :options="$_convertToValueTextPair(webthemePortalGroups)"
                    :textDefaultOption="$t('portalGroupFilter')"
                    :saveInState="true"
                    :value="selectedPortalGroup"
                >
                </x-select>
            </div>
            <b-link class="px-3 py-2" @click="$_clearFilters()">
                {{ $t('clearFilters') }}
            </b-link>
        </div>
        <!-- Overview Table -->
        <div>
            <b-table
                striped
                hover
                small
                outlined
                bordered
                v-show="dataLoaded"
                @row-clicked="$_onDetails"
                :sort-by.sync="sortBy"
                :tbody-tr-class="$_rowClass"
                :items="filteredRecords"
                :fields="fields"
                sort-icon-left
            >
                <template v-slot:cell(urlparameters)="data">
                    <b-link :href="data.item.urlParameters" target="_blank">
                        {{ data.item.urlParameters }}
                    </b-link>
                </template>
            </b-table>
        </div>
        <!-- No content for overview -->
        <div v-show="!dataLoaded" class="col-md-12">
            <span> &nbsp; {{ $t('webThemesNotPresent') }}</span>
        </div>
        <router-links-footer :routerLinks="[{ name: 'webThemeDetailNew' }]" />
    </b-container>
</template>

<script>
    import SearchBox from '../../components/SearchBox.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import RouterLinksFooter from '../../components/dashboard/RouterLinksFooter.vue';
    import {
        stringNotEmpty,
        getWebThemeShortUrl,
        convertToValueTextPair,
        nprogress,
        removeDuplicatesAndSort
    } from '../../services/utilities';

    const alignmentClass = 'text-left align-middle';
    export default {
        name: 'WebThemeOverview',

        components: {
            'search-box': SearchBox,
            'x-select': XSelect,
            'router-links-footer': RouterLinksFooter
        },

        data: function () {
            return {
                fields: [
                    {
                        key: 'name',
                        label: this.$t('columnNameName'),
                        sortable: true,
                        stickyColumn: true,
                        class: alignmentClass,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'type',
                        label: this.$t('columnTypeTitle'),
                        sortable: true,
                        class: alignmentClass,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'themeName',
                        label: this.$t('theme'),
                        sortable: true,
                        class: alignmentClass,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'feedbackThemeName',
                        label: this.$t('feedback'),
                        sortable: true,
                        class: alignmentClass,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'measurementName',
                        label: this.$t('labelMeasure'),
                        sortable: true,
                        class: alignmentClass,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'portalGroup',
                        label: this.$t('columnPortalGroupTitle'),
                        sortable: true,
                        class: alignmentClass,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'number',
                        label: this.$t('columnNumberTitle'),
                        sortable: true,
                        class: alignmentClass,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'urlparameters',
                        label: this.$t('columnUriTitle'),
                        sortable: true,
                        class: alignmentClass,
                        thStyle: { border: 0 }
                    }
                ],
                records: [],
                themesAndFeedbacks: [],
                themeFilterOptions: [],
                feedbackFilterOptions: [],
                measurementFilterOptions: [],
                userInfo: {},
                searchText: '',
                selectedType: '',
                selectedTheme: '',
                selectedFeedback: '',
                selectedMeasurement: '',
                sortBy: 'name',
                selectedPortalGroup: '',
                webthemePortalGroups: [],
                feedbackTypeOptions: [
                    { value: 'themakaart', text: this.$t('themeCard') },
                    { value: 'meldingenkaart', text: this.$t('notificationCard') }
                ]
            };
        },

        mounted() {
            this.$_getWebthemePortalGroups();
        },

        computed: {
            hasSearchValue() {
                return stringNotEmpty(this.searchText);
            },

            dataLoaded() {
                return this.records.length > 0;
            },

            filteredRecords() {
                let records = this.records;
                // filter records on NAME
                if (stringNotEmpty(this.searchText)) {
                    const tokens = this.searchText.toLowerCase();
                    records = records.filter((record) => {
                        return record.name.toLowerCase().includes(tokens);
                    });
                }
                // filter records on type
                if (stringNotEmpty(this.selectedType)) {
                    records = records.filter((record) => {
                        return record.type === this.selectedType;
                    });
                }
                // filter records on theme name
                if (stringNotEmpty(this.selectedTheme)) {
                    records = records.filter((record) => {
                        return (
                            record.themeName.toLowerCase() ===
                            this.selectedTheme.toLowerCase()
                        );
                    });
                }
                // filter records on feedback name
                if (stringNotEmpty(this.selectedFeedback)) {
                    records = records.filter((record) => {
                        return (
                            record.feedbackThemeName.toLowerCase() ===
                            this.selectedFeedback.toLowerCase()
                        );
                    });
                }
                // filter records on measurement name
                if (stringNotEmpty(this.selectedMeasurement)) {
                    records = records.filter((record) => {
                        return (
                            record.measurementName.toLowerCase() ===
                            this.selectedMeasurement.toLowerCase()
                        );
                    });
                }
                // filter records on portal
                if (this.selectedPortalGroup) {
                    records = records.filter((record) => {
                        return record.portalGroup
                            ? record.portalGroup
                                  .split(',')
                                  .indexOf(this.selectedPortalGroup) !== -1
                            : false;
                    });
                }
                return records;
            }
        },

        async beforeCreate() {
            nprogress.start();
            // get all ows data from the backend
            const result = await this.$store.dispatch('useApiData', {
                name: 'GetWebThemeOverview'
            });
            this.records = result?.data;
            nprogress.done();
            // add type column
            this.records = this.records.map((record) => {
                // add type column
                if (record.feedbackTypeId && record.feedbackTypeId > 0) {
                    record.type = 'meldingenkaart';
                } else {
                    record.type = 'themakaart';
                }

                record.urlParameters = getWebThemeShortUrl(record);
                return record;
            });

            // fill array with all theme names, feedback names and measurement names
            this.$_getThemeFeedbackMeasurement();

            // Remove dups and sort records (lodash)
            if (this.feedbackFilterOptions.length > 0) {
                this.feedbackFilterOptions = removeDuplicatesAndSort(
                    this.feedbackFilterOptions
                );
            }

            // Remove dups and sort records (lodash)
            if (this.measurementFilterOptions.length > 0) {
                this.measurementFilterOptions = removeDuplicatesAndSort(
                    this.measurementFilterOptions
                );
            }

            // Remove dups and sort records (lodash)
            if (this.themeFilterOptions.length > 0) {
                this.themeFilterOptions = removeDuplicatesAndSort(
                    this.themeFilterOptions
                );
            }
        },

        created() {
            this.userInfo = this.$store.getters.userInfo;

            // If no portal license, do not show portal column
            if (!this.userInfo.licenses.has_portal_license) {
                this.fields = this.fields.filter(
                    (field) => field.key !== 'portalGroup'
                );
            }
        },

        methods: {
            $_convertToValueTextPair(arr) {
                return convertToValueTextPair(arr);
            },

            $_getThemeFeedbackMeasurement() {
                this.records.forEach((record) => {
                    if (
                        record.feedbackThemeName &&
                        record.feedbackThemeName !== ''
                    ) {
                        this.feedbackFilterOptions.push({
                            value: record.feedbackThemeName,
                            text: record.feedbackThemeName
                        });
                    }
                    if (record.measurementName && record.measurementName !== '') {
                        this.measurementFilterOptions.push({
                            value: record.measurementName,
                            text: record.measurementName
                        });
                    }
                    if (record.themeName && record.themeName !== '') {
                        this.themeFilterOptions.push({
                            value: record.themeName,
                            text: record.themeName
                        });
                    }
                });
            },

            $_onDetails(record) {
                this.$router.push({
                    path: `/dashboard/webTheme/detail/${record.id}`
                });
            },

            async $_getWebthemePortalGroups() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetPortalGroups'
                });
                if (result?.data) {
                    this.webthemePortalGroups = Array.from(result.data);
                }
            },

            $_onSelectPortalGroup(input) {
                this.selectedPortalGroup = input;
            },

            $_onSelectTheme(input) {
                this.selectedTheme = input;
            },

            $_onSelectFeedback(input) {
                this.selectedFeedback = input;
            },

            $_onSelectMeasurement(input) {
                this.selectedMeasurement = input;
            },

            $_onSelectType(input) {
                this.selectedType = input;
            },

            $_onInput(searchText) {
                this.searchText = searchText;
            },

            $_rowClass() {
                return 'tablerow';
            },

            $_clearFilters() {
                this.$_onInput('');
                this.$_onSelectType(null);
                this.$_onSelectTheme(null);
                this.$_onSelectFeedback(null);
                this.$_onSelectMeasurement(null);
                this.$_onSelectPortalGroup(null);
            }
        }
    };
</script>

<style scoped>
.vertical-centre {
    padding-top: 6px;
}

.override-left-padding {
    padding-left: 0px !important;
}
</style>
